import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { CheckCircle2, ChevronDown, ChevronUp, Info } from "lucide-react";
import { startOAuth } from "../../application/oauth";

interface Props {
  /** Show the panel expanded by default (e.g. right after connecting Microsoft). */
  defaultOpen?: boolean;
  /** Whether a Microsoft 365 account is connected at all. */
  connected: boolean;
  /** Whether that account's grant already covers reading transcripts. */
  transcriptsEnabled: boolean;
  onEnableError: () => void;
}

const TEAMS_ADMIN_URL = "https://admin.teams.microsoft.com/policies/meetings";
const TEAMS_TRANSCRIPTION_HELP_URL =
  "https://support.microsoft.com/office/view-live-transcription-in-microsoft-teams-meetings-dc1a8f23-2e20-4684-885e-2152e06a4a8b";

/**
 * What a Teams session needs before its transcript can become a draft.
 *
 * Reading transcripts is a second consent, asked for only from here: it needs
 * the tenant admin's approval, and asking for it on the first connection
 * would stop therapists without that approval from creating meetings at all.
 */
export default function TeamsTranscriptRequirements({
  defaultOpen = false,
  connected,
  transcriptsEnabled,
  onEnableError,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const [starting, setStarting] = useState(false);

  const requirements: string[] = t("scheduler.settings.teamsAI.requirements", {
    returnObjects: true,
  }) as string[];

  const enable = async () => {
    setStarting(true);
    try {
      // Navigates away on success, so `starting` is only reset on failure.
      await startOAuth("microsoft", { transcripts: true });
    } catch {
      setStarting(false);
      onEnableError();
    }
  };

  return (
    <div className="rounded-xl border border-accent/20 bg-accent-tint">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-accent-text">
          <Info size={15} className="shrink-0" />
          {t("scheduler.settings.teamsAI.title")}
        </span>
        {open ? (
          <ChevronUp size={15} className="shrink-0 text-accent-text" />
        ) : (
          <ChevronDown size={15} className="shrink-0 text-accent-text" />
        )}
      </button>

      {open && (
        <div className="border-t border-accent/20 px-4 pb-4 pt-3">
          <p className="mb-3 text-xs text-accent-text">
            {t("scheduler.settings.teamsAI.description")}
          </p>

          <ul className="flex flex-col gap-2">
            {Array.isArray(requirements) &&
              requirements.map((_, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-accent-on-tint">
                  <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-accent-text" />
                  <span>
                    <Trans
                      i18nKey={`scheduler.settings.teamsAI.requirements.${i}`}
                      components={{ strong: <strong /> }}
                    />
                  </span>
                </li>
              ))}
          </ul>

          {connected && (
            <div className="mt-4">
              {transcriptsEnabled ? (
                <p role="status" className="flex items-center gap-2 text-xs font-medium text-accent-text">
                  <CheckCircle2 size={14} className="shrink-0" />
                  {t("scheduler.settings.teamsAI.enabled")}
                </p>
              ) : (
                <button
                  type="button"
                  disabled={starting}
                  onClick={enable}
                  className="rounded-lg border border-brand-500 px-3 py-1.5 text-sm font-medium text-brand-600 disabled:opacity-50 dark:text-brand-400"
                >
                  {starting
                    ? t("scheduler.settings.connecting")
                    : t("scheduler.settings.teamsAI.enable")}
                </button>
              )}
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-3">
            <a
              href={TEAMS_ADMIN_URL}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-medium text-accent-text underline"
            >
              {t("scheduler.settings.teamsAI.linkAdmin")}
            </a>
            <a
              href={TEAMS_TRANSCRIPTION_HELP_URL}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-medium text-accent-text underline"
            >
              {t("scheduler.settings.teamsAI.linkHelp")}
            </a>
          </div>

          <p className="mt-3 text-xs text-accent-text">
            {t("scheduler.settings.teamsAI.outcome")}
          </p>
        </div>
      )}
    </div>
  );
}
